import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import { AuthShell, Field } from "./Login";
import { CheckCircle2, Lock } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const strength = useMemo(() => {
    if (!pw) return { score: 0, label: "" };
    let s = 0;
    if (pw.length >= 8) s += 1;
    if (pw.length >= 12) s += 1;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s += 1;
    if (/\d/.test(pw)) s += 1;
    if (/[^A-Za-z0-9]/.test(pw)) s += 1;
    const labels = ["Très faible", "Faible", "Moyen", "Bon", "Fort", "Excellent"];
    return { score: s, label: labels[Math.min(s, 5)] };
  }, [pw]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Minimum 8 caractères"); return; }
    if (pw !== confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Réinitialisation échouée");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthShell>
        <h1 className="font-display text-2xl text-[#1F2937]">Lien invalide</h1>
        <p className="mt-2 text-sm text-[#5B5B62]">Le lien de réinitialisation est invalide ou incomplet.</p>
        <Link to="/forgot-password" className="mt-4 inline-block text-[#0B2545] font-medium hover:underline">Demander un nouveau lien</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#1F2937]">Nouveau mot de passe</h1>
      <p className="mt-2 text-sm text-[#5B5B62]">Choisissez un mot de passe fort — au moins 8 caractères.</p>
      {done ? (
        <div className="mt-8 border border-emerald-200 bg-emerald-50 rounded-md p-4 text-sm text-emerald-800" data-testid="reset-success">
          <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="h-4 w-4" /><b>Mot de passe réinitialisé</b></div>
          Vous allez être redirigé vers la connexion…
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4" noValidate>
          <Field label="Nouveau mot de passe">
            <input
              data-testid={AUTH.resetPassword}
              type="password" required minLength={8}
              value={pw} onChange={(e) => setPw(e.target.value)}
              className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
            />
          </Field>
          {pw && (
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1 bg-[#E4E1D8] rounded overflow-hidden">
                <div className={`h-full transition-all ${
                  strength.score <= 1 ? "bg-red-500 w-1/5" :
                  strength.score === 2 ? "bg-orange-400 w-2/5" :
                  strength.score === 3 ? "bg-amber-400 w-3/5" :
                  strength.score === 4 ? "bg-emerald-400 w-4/5" :
                  "bg-emerald-600 w-full"
                }`} />
              </div>
              <div className="text-[10px] font-mono text-[#5B5B62] w-20">{strength.label}</div>
            </div>
          )}
          <Field label="Confirmer le mot de passe" error={confirm && pw !== confirm ? "Les mots de passe ne correspondent pas" : ""}>
            <input
              data-testid={AUTH.resetConfirm}
              type="password" required minLength={8}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
            />
          </Field>
          <button
            data-testid={AUTH.resetSubmit}
            type="submit" disabled={busy}
            className="w-full rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white font-medium py-2.5 transition-colors flex items-center justify-center gap-2"
          >
            <Lock className="h-3.5 w-3.5" strokeWidth={1.8} />
            {busy ? "Réinitialisation…" : "Réinitialiser"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
